import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { productosService, Producto, CreateProductoDto, UpdateProductoDto } from '../src/services/productosService';
import { negociosService, Negocio } from '../src/services/negociosService';
import { empresasService, Empresa } from '../src/services/empresasService';
import { useAuth } from '../src/contexts/AuthContext';

interface ProductosProps {
  onClose: () => void;
}

type SortableKey = 'nombre' | 'precio' | 'esDelDia' | 'estaActivo';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
};

const Productos: React.FC<ProductosProps> = ({ onClose }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.nombreRol === 'SuperAdmin';

  const [productos, setProductos] = useState<Producto[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [negocios, setNegocios] = useState<Negocio[]>([]);
  const [negociosFiltrados, setNegociosFiltrados] = useState<Negocio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingProducto, setEditingProducto] = useState<Producto | null>(null);
  const [formData, setFormData] = useState<CreateProductoDto>({
    negocioId: 0,
    nombre: '',
    descripcion: null,
    precio: 0,
    imagenUrl: null,
    esDelDia: false,
    estaActivo: true,
  });
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Default to user's empresa if not SuperAdmin
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | 'all'>(user?.empresaId ?? 'all');
  const [selectedNegocioId, setSelectedNegocioId] = useState<number | 'all'>('all');
  const [sortConfig, setSortConfig] = useState<{ key: SortableKey; direction: 'asc' | 'desc' } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [formEmpresaId, setFormEmpresaId] = useState<number>(0);
  const [formNegociosFiltrados, setFormNegociosFiltrados] = useState<Negocio[]>([]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes disminuir { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.001); opacity: 0; } }
      @keyframes crecer { 0% { transform: scale(0.001); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const isDarkMode = () => {
    const theme = localStorage.getItem('theme');
    if (theme) return theme === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const showAuthAlert = () => {
    const dark = isDarkMode();
    Swal.fire({
      icon: 'warning',
      title: 'Acceso Denegado',
      text: 'El usuario no está logueado',
      confirmButtonColor: '#f97316',
      background: dark ? '#1a1614' : '#ffffff',
      color: dark ? '#ffffff' : '#1a1614',
      customClass: {
        popup: 'rounded-2xl'
      }
    }).then(() => {
      onClose();
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      showAuthAlert();
      return;
    }
    loadData();
  }, []);

  useEffect(() => {
    if (user?.empresaId && !isSuperAdmin) {
      setSelectedEmpresaId(user.empresaId);
    }
  }, [user?.empresaId, isSuperAdmin]);

  useEffect(() => {
    if (selectedEmpresaId !== 'all') {
      const filtered = negocios.filter(n => n.empresaId === selectedEmpresaId);
      setNegociosFiltrados(filtered);
      if (!filtered.find(n => n.negocioId === selectedNegocioId)) {
        setSelectedNegocioId('all');
      }
    } else {
      setNegociosFiltrados(negocios);
    }
  }, [selectedEmpresaId, negocios]);

  useEffect(() => {
    if (formEmpresaId > 0) {
      const filtered = negocios.filter(n => n.empresaId === formEmpresaId);
      setFormNegociosFiltrados(filtered);
    } else {
      setFormNegociosFiltrados([]);
    }
  }, [formEmpresaId, negocios]);

  const getFullImageUrl = (url: string | null) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:7202/api').replace('/api', '');
    return `${apiBase}${url}`;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [productosData, negociosData, empresasData] = await Promise.all([
        productosService.getAll(),
        negociosService.getAll(),
        empresasService.getAll(),
      ]);
      setProductos(productosData);
      setNegocios(negociosData);
      setEmpresas(empresasData);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error al cargar datos';
      if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
        showAuthAlert();
        return;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const filteredProductos = useMemo(() => {
    const lowered = searchTerm.toLowerCase();
    let filtered = productos.filter(p =>
      p.nombre.toLowerCase().includes(lowered) ||
      (p.descripcion && p.descripcion.toLowerCase().includes(lowered))
    );
    if (selectedNegocioId !== 'all') {
      filtered = filtered.filter(p => p.negocioId === selectedNegocioId);
    } else if (selectedEmpresaId !== 'all') {
      const negocioIds = negocios.filter(n => n.empresaId === selectedEmpresaId).map(n => n.negocioId);
      filtered = filtered.filter(p => negocioIds.includes(p.negocioId));
    }
    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        const dir = sortConfig.direction === 'asc' ? 1 : -1;
        if (sortConfig.key === 'estaActivo' || sortConfig.key === 'esDelDia') {
          return (a[sortConfig.key] === b[sortConfig.key] ? 0 : a[sortConfig.key] ? -1 : 1) * dir;
        }
        const aVal: any = (a as any)[sortConfig.key] ?? '';
        const bVal: any = (b as any)[sortConfig.key] ?? '';
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      });
    }
    return filtered;
  }, [productos, searchTerm, sortConfig, selectedEmpresaId, selectedNegocioId, negocios]);

  const handleSort = (key: SortableKey) => {
    setSortConfig(prev => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleCreate = () => {
    setEditingProducto(null);
    // Default to selectedEmpresaId, or first active empresa for SuperAdmin
    const empresaId = isSuperAdmin 
      ? (selectedEmpresaId !== 'all' ? selectedEmpresaId : (empresas.find(e => e.activa)?.empresaId ?? 0))
      : (user?.empresaId ?? 0);
    const negocioId = selectedNegocioId === 'all' ? 0 : selectedNegocioId;
    setFormEmpresaId(empresaId);
    setFormData({
      negocioId: negocioId,
      nombre: '',
      descripcion: null,
      precio: 0,
      imagenUrl: null,
      esDelDia: false,
      estaActivo: true,
    });
    setSelectedFile(null);
    setImagePreview(null);
    setIsModalOpen(true);
  };

  const handleEdit = (producto: Producto) => {
    setEditingProducto(producto);
    const negocio = negocios.find(n => n.negocioId === producto.negocioId);
    setFormEmpresaId(negocio?.empresaId || 0);
    setFormData({
      negocioId: producto.negocioId,
      nombre: producto.nombre,
      descripcion: producto.descripcion,
      precio: producto.precio,
      imagenUrl: producto.imagenUrl,
      esDelDia: producto.esDelDia,
      estaActivo: producto.estaActivo,
    });
    setSelectedFile(null);
    setImagePreview(getFullImageUrl(producto.imagenUrl));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsModalClosing(false);
      setEditingProducto(null);
    }, 300);
  };

  const handleDelete = async (producto: Producto) => {
    const result = await Swal.fire({
      title: '¿Eliminar producto?',
      html: `Esta acción eliminará el producto <strong>${producto.nombre}</strong>.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false,
      customClass: {
        popup: 'rounded-2xl'
      }
    });
    if (!result.isConfirmed) return;
    try {
      await productosService.delete(producto.productoId);
      setProductos(productos.filter(p => p.productoId !== producto.productoId));
      Swal.fire({ 
        title: 'Producto eliminado', 
        icon: 'success', 
        background: 'rgba(15,23,42,0.85)', 
        color: '#fff', 
        confirmButtonColor: '#3b82f6', 
        timer: 2000, 
        timerProgressBar: true, 
        backdrop: false, 
        customClass: { 
          popup: 'rounded-2xl' 
        } 
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nombre.trim()) return;
    if (formData.negocioId <= 0) {
      setError('Debe seleccionar un negocio');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      let savedProducto: Producto;
      if (editingProducto) {
        savedProducto = await productosService.update(editingProducto.productoId, formData as UpdateProductoDto);
      } else {
        savedProducto = await productosService.create(formData);
      }

      if (selectedFile) {
        const uploadResult = await productosService.uploadImage(savedProducto.productoId, selectedFile);
        savedProducto = { ...savedProducto, imagenUrl: uploadResult.imagenUrl };
      }

      if (editingProducto) {
        setProductos(productos.map(p => p.productoId === savedProducto.productoId ? savedProducto : p));
        Swal.fire({ title: 'Producto actualizado', icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 2000, timerProgressBar: true, backdrop: false, customClass: { popup: 'rounded-2xl' } });
      } else {
        setProductos([savedProducto, ...productos]);
        Swal.fire({ title: 'Producto creado', icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 2000, timerProgressBar: true, backdrop: false, customClass: { popup: 'rounded-2xl' } });
      }
      closeModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else if (name === 'precio') {
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else if (name === 'negocioId') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value || null }));
    }
  };

  return (
    <div className="pt-4 fixed inset-0 z-50 bg-white dark:bg-background-dark overflow-auto" style={{ animation: 'crecer 0.3s ease-out' }}>
      <div className="dark:bg-background-dark text-gray-900 dark:text-white px-4 sm:px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="flex items-center gap-2 text-gray-900 dark:text-white/80 hover:text-gray-900 dark:hover:text-white transition-colors">
            <span className="material-symbols-outlined">arrow_back</span>
            <span className="hidden sm:inline">Volver</span>
          </button>
          <div>
            <h1 className="text-2xl text-secondary dark:text-white">Productos</h1>
            <p className="text-gray-900 dark:text-white text-sm hidden sm:block">Administra los productos registrados</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="bg-[#307CAF] dark:bg-slate-800 text-white px-4 sm:px-6 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
              <input type="text" placeholder="Buscar por nombre o descripción..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-white/20 dark:border-slate-600 rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-white/50 focus:border-transparent" />
            </div>
          </div>
          {isSuperAdmin && (
            <div className="sm:w-48">
              <select value={selectedEmpresaId} onChange={(e) => { setSelectedEmpresaId(e.target.value === 'all' ? 'all' : Number(e.target.value)); setSelectedNegocioId('all'); }}
                className="w-full px-3 py-2 border border-white/20 dark:border-slate-600 rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-white/50 focus:border-transparent">
                <option value="all">Todas las empresas</option>
                {empresas.map((empresa) => (<option key={empresa.empresaId} value={empresa.empresaId}>{empresa.nombreEmpresa}</option>))}
              </select>
            </div>
          )}
          <div className="sm:w-48">
            <select value={selectedNegocioId} onChange={(e) => setSelectedNegocioId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="w-full px-3 py-2 border border-white/20 dark:border-slate-600 rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-white/50 focus:border-transparent">
              <option value="all">Todos los negocios</option>
              {negociosFiltrados.map((negocio) => (<option key={negocio.negocioId} value={negocio.negocioId}>{negocio.nombreNegocio}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span>{filteredProductos.length} productos</span>
            <button onClick={handleCreate} className="inline-flex items-center gap-1 px-4 py-2 bg-white text-[#307CAF] dark:bg-slate-600 dark:text-white rounded-full font-semibold hover:bg-white/90 dark:hover:bg-slate-500 transition">
              <span className="material-symbols-outlined text-base">add</span>Nuevo Producto
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl text-red-600 dark:text-red-400 text-sm">{error}</div>
        )}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              <div className="animate-spin w-8 h-8 border-4 border-[#307CAF] border-t-transparent rounded-full mx-auto mb-4"></div>Cargando productos...
            </div>
          ) : filteredProductos.length === 0 ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              <span className="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600 mb-4 block">inventory_2</span>No se encontraron productos con ese criterio.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-[#307CAF] text-white dark:bg-slate-700">
                  <tr>
                    <th onClick={() => handleSort('nombre')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Nombre{sortConfig?.key === 'nombre' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Negocio</th>
                    <th onClick={() => handleSort('precio')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Precio{sortConfig?.key === 'precio' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th onClick={() => handleSort('esDelDia')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Del Día{sortConfig?.key === 'esDelDia' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th onClick={() => handleSort('estaActivo')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Estado{sortConfig?.key === 'estaActivo' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {filteredProductos.map((producto) => (
                    <tr key={producto.productoId} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {producto.imagenUrl ? (
                            <img src={getFullImageUrl(producto.imagenUrl)!} alt={producto.nombre} className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-[#307CAF]/10 flex items-center justify-center">
                              <span className="material-symbols-outlined text-[#307CAF] text-sm">fastfood</span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-white">{producto.nombre}</p>
                            {producto.descripcion && <p className="text-sm text-slate-500 dark:text-slate-400 truncate max-w-[200px]">{producto.descripcion}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{producto.nombreNegocio || negocios.find(n => n.negocioId === producto.negocioId)?.nombreNegocio || `ID: ${producto.negocioId}`}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900 dark:text-white">{formatCurrency(producto.precio)}</td>
                      <td className="px-6 py-4">
                        {producto.esDelDia ? (
                          <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            <span className="material-symbols-outlined text-sm">wb_sunny</span>Del día
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${producto.estaActivo ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>
                          <span className="material-symbols-outlined text-sm">{producto.estaActivo ? 'check_circle' : 'cancel'}</span>{producto.estaActivo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleEdit(producto)} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-slate-600 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700">
                            <span className="material-symbols-outlined text-sm">edit</span>Editar
                          </button>
                          <button onClick={() => handleDelete(producto)} className="inline-flex items-center gap-1 rounded-lg border border-transparent px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                            <span className="material-symbols-outlined text-sm">delete</span>Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="relative w-full max-w-2xl rounded-2xl bg-white/95 dark:bg-slate-800 p-6 shadow-xl backdrop-blur-sm border border-gray-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto" style={{ animation: isModalClosing ? 'disminuir 0.3s ease-out' : 'crecer 0.3s ease-out' }}>
            <button onClick={closeModal} className="absolute right-3 top-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white">
              <span className="material-symbols-outlined">close</span>
            </button>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">{editingProducto ? 'Editar Producto' : 'Crear Producto'}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{editingProducto ? 'Modifica los datos del producto.' : 'Añade los datos del nuevo producto.'}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className={`grid gap-4 ${isSuperAdmin ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {isSuperAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Empresa *</label>
                    <select value={formEmpresaId} onChange={(e) => { setFormEmpresaId(Number(e.target.value)); setFormData({ ...formData, negocioId: 0 }); }} required disabled={!!editingProducto}
                      className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]">
                      <option value={0}>Seleccionar empresa...</option>
                      {empresas.map((empresa) => (<option key={empresa.empresaId} value={empresa.empresaId}>{empresa.nombreEmpresa}</option>))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Negocio *</label>
                  <select value={formData.negocioId} onChange={(e) => setFormData({ ...formData, negocioId: Number(e.target.value) })} required disabled={!!editingProducto || formEmpresaId === 0}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF] disabled:opacity-50">
                    <option value={0}>Seleccionar negocio...</option>
                    {formNegociosFiltrados.map((negocio) => (<option key={negocio.negocioId} value={negocio.negocioId}>{negocio.nombreNegocio}</option>))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Nombre del Producto *</label>
                <input type="text" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} required maxLength={150}
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Descripción</label>
                <textarea value={formData.descripcion || ''} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value || null })} rows={2} maxLength={300}
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Precio *</label>
                  <input type="number" step="0.01" min="0" value={formData.precio} onChange={(e) => setFormData({ ...formData, precio: parseFloat(e.target.value) || 0 })} required
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Imagen (opcional)</label>
                  <div className="flex items-center gap-4">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="h-16 w-16 rounded-lg object-cover border-2 border-slate-200 dark:border-slate-700" />
                    ) : (
                      <div className="h-16 w-16 rounded-lg bg-[#307CAF]/10 flex items-center justify-center">
                        <span className="material-symbols-outlined text-[#307CAF] text-2xl">fastfood</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-gray-200 dark:border-slate-600 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors">
                        <span className="material-symbols-outlined text-base">upload</span>
                        {selectedFile ? selectedFile.name : 'Seleccionar imagen'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">JPG, PNG o WEBP. Máx 5 MB.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6 rounded-xl border border-gray-200 dark:border-slate-600 px-4 py-3">
                <label className="inline-flex items-center cursor-pointer gap-2">
                  <input type="checkbox" checked={formData.esDelDia} onChange={(e) => setFormData({ ...formData, esDelDia: e.target.checked })} className="sr-only" />
                  <span className={`relative inline-block h-6 w-11 rounded-full transition ${formData.esDelDia ? 'bg-amber-500' : 'bg-slate-400'}`}>
                    <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${formData.esDelDia ? 'translate-x-5' : ''}`} />
                  </span>
                  <span className="text-sm font-medium text-[#307CAF] dark:text-slate-200">Del Día</span>
                </label>
                <label className="inline-flex items-center cursor-pointer gap-2">
                  <input type="checkbox" checked={formData.estaActivo} onChange={(e) => setFormData({ ...formData, estaActivo: e.target.checked })} className="sr-only" />
                  <span className={`relative inline-block h-6 w-11 rounded-full transition ${formData.estaActivo ? 'bg-[#307CAF]' : 'bg-slate-400'}`}>
                    <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${formData.estaActivo ? 'translate-x-5' : ''}`} />
                  </span>
                  <span className="text-sm font-medium text-[#307CAF] dark:text-slate-200">Activo</span>
                </label>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" className="rounded-lg border border-gray-200 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={closeModal}>Cancelar</button>
                <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#307CAF] px-4 py-2 text-sm font-semibold text-white shadow hover:bg-[#307CAF]/90 disabled:opacity-60">
                  {saving ? (<><span className="material-symbols-outlined animate-spin">progress_activity</span>Guardando...</>) : (<><span className="material-symbols-outlined">save</span>Guardar</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Productos;